import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../context/LanguageContext";

function PopupModal({ content, onClose }) {
  const isImage = content?.type === "img";

  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [initialLoad, setInitialLoad] = useState(true);

  // Calculate initial scale to fit the full image
  useEffect(() => {
    if (isImage && initialLoad) {
      const modalWidth = window.innerWidth * 0.9;
      const modalHeight = window.innerHeight * 0.9;
      const maxWidth = Math.min(450, modalWidth);

      // Give a moment for the image to load, then fit to container
      setTimeout(() => {
        const img = document.querySelector('.popup-image');
        if (img) {
          const scaleX = (maxWidth - 32) / img.naturalWidth;
          const scaleY = (modalHeight - 32) / img.naturalHeight;
          const fitScale = Math.min(scaleX, scaleY, 1);
          setScale(fitScale);
        }
        setInitialLoad(false);
      }, 50);
    }
  }, [isImage, initialLoad]);

  const handleWheel = (e) => {
    if (!isImage) return;
    e.preventDefault();
    let newScale = scale + e.deltaY * -0.001;
    newScale = Math.min(Math.max(0.5, newScale), 4);
    setScale(newScale);
  };

  const handleMouseDown = (e) => {
    if (!isImage) return;
    e.preventDefault();
    setDragging(true);
    setStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    setPos({ x: e.clientX - start.x, y: e.clientY - start.y });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="p-4 w-[90vw] max-w-[450px] max-h-[90vh] rounded-lg relative overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-red-700 text-2xl z-50"
        >
          <XMarkIcon className="h-10 w-10" />
        </button>

        {/* Content wrapper */}
        <div
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          className={isImage ? "w-full h-full cursor-grab active:cursor-grabbing" : ""}
          style={{
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          <div
            style={{
              transform: isImage
                ? `translate(${pos.x}px, ${pos.y}px) scale(${scale})`
                : "none",
              transition: dragging ? "none" : "transform 0.15s ease-out",
            }}
          >
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

function BoardPage() {
  const [activeChar, setActiveChar] = useState(null);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const characters = [
    { id: "monie", src: "/board/monie.webp", top: 435, right: 535 },
    { id: "wynn", src: "/board/wynn.webp", top: 410, right: 430 },
    { id: "jo", src: "/board/jo.webp", top: 430, right: 340 },
    { id: "den", src: "/board/den.webp", top: 390, right: 245 },
  ];

  return (
    <div className="w-screen h-screen overflow-auto 2xl:overflow-hidden">
      <div
        className="
      relative
      w-[1600px]
      h-[900px]
      mx-auto
    "
      >
        {/* BACKGROUND */}
        <img
          src="/bg/showcase.webp"
          className="absolute w-full h-full object-cover object-center"
          alt="background"
        />
        <img src="/board/board.webp" className="absolute z-0 w-4/5 left-[8%] pt-[1.5%]" />
        <motion.div
          className="absolute top-[350px] left-[220px] w-[21%] z-10"
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate("/showcase")}
        >
          <img src="/board/showcase.webp" alt="showcase" className="w-full h-auto" />
        </motion.div>

        {characters.map((char) => (
          <motion.div
            key={char.id}
            className="absolute z-10 cursor-pointer"
            style={{ top: char.top, right: char.right, width: char.id === "wynn" ? "8%" : "7%" }}
            whileHover={{ scale: 1.05 }}
            onClick={() => setActiveChar(char.id)}
          >
            <img src={char.src} alt={char.id} className="w-full h-auto" />
          </motion.div>
        ))}

        <motion.div
          className="absolute w-[19%] top-[100px] left-[200px] z-10"
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate("/condelia")}
        >
          <img src="/board/condelia.webp" className="w-full h-auto" />
        </motion.div>

        <img src={lang == "TH" ? "/board/news_th.webp" : "/board/news_en.webp"} className="absolute z-0 w-[25%] top-[40px] left-[1000px]" />
        <img src="/board/pin.webp" className="absolute z-20 w-[55%] pt-[2%] top-[90px] left-[395px] pointer-events-none" />
        <img
          src="/board/shadow.webp"
          className="absolute w-full h-full object-cover z-30 pointer-events-none"
          alt="shadow"
        />

        {activeChar && (
          <PopupModal
            content={
              <img
                src={`/v/${activeChar}_${lang == "TH" ? "thai" : "eng"}.svg`}
                alt={activeChar}
              />
            }
            onClose={() => setActiveChar(null)}
          />
        )}
      </div>
    </div>
  );
}

export default BoardPage;
